import { InitOptions, Resource, createInstance } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";

import { setGlobalContextProperty } from "datadog-loader/loader";

import type { getSplitIOFlag } from "splitio-loader/src";

import combinedLang from "./combined.json";

const languages = [
  "en-US",
  "es-US",
  "en-GB",
  "fr-FR",
  "en-CA",
  "fr-CA",
  "es-ES",
  "nl-NL",
  "de-DE",
  "pt-BR",
  "pt-PT",
];

export const allLanguages = [...languages];

type StartI18NProps = {
  namespaces?: string[];
  loadPathPrefix?: string;
  lookupQuerystring?: string;
  lookupLocalStorage?: string;
  defaultNS?: string;
  supportedLngs?: string[];
  getSplitIOFlagInstance?: typeof getSplitIOFlag;
  preload?: boolean;
  resources?: Resource;
};

export const startI18N = ({
  namespaces = ["combined"],
  loadPathPrefix = "",
  lookupQuerystring = "lng",
  lookupLocalStorage = "i18nextLng",
  defaultNS = "combined",
  supportedLngs,
  preload = false,
  resources = {},
}: StartI18NProps) => {
  let supportedLngsFinal = allLanguages;

  if (supportedLngs) {
    supportedLngsFinal = supportedLngs;
  }

  const config: InitOptions = {
    ns: namespaces,
    fallbackLng: "en-US",
    debug: false,
    defaultNS,
    supportedLngs: supportedLngsFinal, // This needs to be updated as we add more languages
    interpolation: {
      escapeValue: false,
    },
    backend: {
      loadPath: `${loadPathPrefix}/locales/{{lng}}/{{ns}}.json`,
    },
    detection: {
      lookupQuerystring,
      lookupLocalStorage,
    },
    partialBundledLanguages: true,
    resources: Object.keys(resources).length
      ? resources
      : { "en-US": { combined: { ...combinedLang } } }, // always load default en-US
  };
  if (preload) {
    config.preload = supportedLngsFinal;
  }

  const i18nInstance = createInstance(config);

  void i18nInstance
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init();

  // This is to set the <html lang=xxx > attribute for ADA/A11y purposes.
  i18nInstance.on("languageChanged", (lng) => {
    if (typeof window !== "undefined") {
      document.documentElement.setAttribute("lang", lng);
    }
    setGlobalContextProperty({
      name: "language",
      context: lng,
    });
  });

  return i18nInstance;
};
